import React from 'react';

import { Translate } from 'react-localize-redux';

import { AnimatePresence } from 'framer-motion';

import MotionOpacity from '@components/motion/motion-opacity';
import { getStreamingPlatforms } from '../../artist/artist-editor';
import StreamingPlatformCtrl from '../../form/streaming-platform-list';

import {
  ArtistExternalIds,
  ArtistPlatformRecord,
} from '@common/types/artist/simple-artist';

import {
  validatePlatform,
  validateRequiredPlatform,
  validateSpotifyUrl,
} from '@webapp/utils/validators/artist';
import { StreamingPlatform } from '@common/types/data/streaming-platform';
import { ErrorMessage } from '@components/basics/error-message';
import { ArtistPlatforms } from '@common/types/artist/artist-platforms';

interface UpdateStreamingPlatformsCtrlProps {
  className?: string;
  artistName: string;
  platforms?: ArtistPlatformRecord;
  externalIds?: ArtistExternalIds;
  dataType: 'artist';
  showErrors?: boolean;
  dontHide?: boolean;
  lockChangeProfile?: boolean;
  onChange: (platforms: ArtistPlatformRecord) => void;
}

const UpdateStreamingPlatformsCtrl: React.FC<
  UpdateStreamingPlatformsCtrlProps
> = ({
  artistName,
  platforms,
  externalIds,
  dataType,
  dontHide,
  onChange,
  lockChangeProfile,
  ...props
}) => {
  const filterPlatforms = () => {
    if (!platforms) {
      return [];
    }
    if (!externalIds) {
      return [];
    }

    if (dontHide) {
      return [];
    }

    return Object.keys(platforms).filter(
      (key) =>
        !externalIds[key as ArtistPlatforms] ||
        !platforms[key as ArtistPlatforms] ||
        !validatePlatform(platforms[key as ArtistPlatforms])
    );
  };

  const getErrorMessage = (platform: StreamingPlatform) => {
    if (platform.platform === 'spotify') {
      if (!validateRequiredPlatform(platform.handle)) {
        return (
          <MotionOpacity>
            <ErrorMessage
              className="mb-10"
              errorId="ONBOARDING.ARTIST.ERROR.STREAMING"
            />
          </MotionOpacity>
        );
      }
      if (platform.handle?.length && !validateSpotifyUrl(platform.handle)) {
        return (
          <MotionOpacity>
            <ErrorMessage
              className="mb-10"
              errorId="ONBOARDING.ARTIST.ERROR.SPOTIFY_URL"
            />
          </MotionOpacity>
        );
      }
    } else {
      const isValid = validatePlatform(platform.handle);
      return isValid ? undefined : (
        <MotionOpacity>
          <ErrorMessage
            className="mb-10"
            errorId="ONBOARDING.ARTIST.ERROR.URL"
          />
        </MotionOpacity>
      );
    }
  };

  const addValidation = (plat: StreamingPlatform[]) => {
    if (props.showErrors) {
      return plat.map((platform) => {
        return {
          ...platform,
          errorMessage: getErrorMessage(platform),
        };
      });
    }
    return plat;
  };

  const handleChange = (update?: StreamingPlatform[]) => {
    const newPlatforms: ArtistPlatformRecord = update
      ? {
          ...platforms,
          ...update.reduce((memo: ArtistPlatformRecord, platform) => {
            return {
              ...memo,
              [platform.platform]: platform.handle,
            };
          }, {}),
        }
      : {};

    onChange(newPlatforms);
  };

  const availablePlatforms = addValidation(getStreamingPlatforms(platforms));

  return (
    <AnimatePresence>
      <MotionOpacity>
        {availablePlatforms ? (
          <div className={props.className}>
            <StreamingPlatformCtrl
              className="mt-30"
              disableSelect
              filterKeys={filterPlatforms()}
              platforms={availablePlatforms}
              lockChangeProfile={lockChangeProfile}
              title=" "
              text={<Translate id="RELEASE.ARTIST.STREAMING_PLATFORMS.TEXT" />}
              onChange={(newPlatforms) => handleChange(newPlatforms)}
            />
          </div>
        ) : (
          <div></div>
        )}
      </MotionOpacity>
    </AnimatePresence>
  );
};

export default UpdateStreamingPlatformsCtrl;
